import React, { useEffect, type ReactElement } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import AssistantViewController from '../AssistantView/AssistantViewController';
import styles from './ArtifactPanel.module.css';
import classnames from 'classnames';
import MessageContent from '../ConversationPanel/MessageContent';
import SourceView from './SourceView';
import ArtifactHeader, { CONTENT_TYPE_SUPPORTS_SOURCE } from './ArtifactHeader';
import ResizerHandle from './ResizerHandle';
import { allowReactContentPreview } from '../utils/allowReactContentPreview';
import {
  type AssistantArtifact,
  AssistantToolCall,
} from 'components/framework/models';

// Content types where the panel should shrink. Otheriwse it will use the avaiable
// height of the screen.
const SHRINKING_CONTENT_TYPES = ['application/vnd.aha.html'];

// Content types without padding.
const NON_PADDING_CONTENT_TYPES = [
  'text/html',
  'application/vnd.aha.bookmark',
  'application/vnd.aha.react',
  'application/vnd.aha.react_entrypoint',
];

const ArtifactPanel: React.FC = () => {
  const [width, setWidth] = React.useState<number | undefined>(undefined);
  const [resizing, setResizing] = React.useState(false);
  const artifactPanelRef = React.useRef<HTMLDivElement>(null);
  const artifactPanelParentRef = React.useRef<HTMLDivElement>(null);

  const controller = useController(AssistantViewController);
  const { size, currentArtifact, artifactDisplayMode } = controller.state;
  let toolCall: AssistantToolCall | null = null;
  let artifact: AssistantArtifact | null = null;

  if (currentArtifact && AssistantToolCall.is(currentArtifact)) {
    toolCall = currentArtifact;
    artifact = toolCall?.content?.panel as AssistantArtifact | null;
  } else {
    artifact = currentArtifact as AssistantArtifact | null;
  }

  const isFloating = [
    'full-height',
    'engaged-top',
    'engaged-bottom',
    'side-panel',
    'toolbar',
  ].includes(size);
  let content: ReactElement | null = null;
  let hidden = false;

  const addPopoverAttributes = () => {
    const panel = artifactPanelRef.current;
    if (!panel) return;

    panel.popover = 'manual';
    const parent = artifactPanelParentRef.current?.parentElement;

    if (parent) {
      const parentRect = parent.getBoundingClientRect();
      panel.style.top = `${parentRect.top}px`;
      panel.style.right = `${window.innerWidth - parentRect.left - 5}px`;
      panel.style.left = 'initial';
      panel.style.maxHeight = `calc(100vh - ${parentRect.top + 20}px)`;
    }

    panel.showPopover();
  };

  const removePopoverAttributes = () => {
    const panel = artifactPanelRef.current;
    if (!panel) return;

    if (panel.popover) {
      panel.hidePopover();
      panel.popover = null;
    }

    panel.style.top = '';
    panel.style.right = '';
    panel.style.left = '';
    panel.style.maxHeight = '';
  };

  useEffect(() => {
    if (hidden || !artifact) {
      removePopoverAttributes();
    } else if (isFloating && artifactPanelRef.current) {
      addPopoverAttributes();
      setTimeout(() => addPopoverAttributes(), 200);
    } else {
      removePopoverAttributes();
    }
  }, [size, hidden, artifact, isFloating]);

  const validToolCallAndSize =
    artifact &&
    [
      'full-height',
      'full-screen',
      'full-page',
      'side-panel',
      'engaged-top',
    ].includes(size);

  if (validToolCallAndSize && artifact) {
    const isSourceView =
      artifactDisplayMode === 'source' ||
      (!allowReactContentPreview(toolCall, artifact) &&
        CONTENT_TYPE_SUPPORTS_SOURCE.includes(artifact.contentType || ''));

    if (isSourceView && toolCall) {
      content = (
        <React.Fragment key={artifact.uniqueId}>
          <ArtifactHeader toolCall={toolCall} artifact={artifact} />
          <div className={styles.artifactBody}>
            <SourceView toolCall={toolCall} />
          </div>
        </React.Fragment>
      );
    } else {
      const needsPadding = !NON_PADDING_CONTENT_TYPES.includes(
        artifact.contentType || ''
      );

      content = (
        <React.Fragment
          key={toolCall?.uniqueId || artifact.title || 'artifact'}
        >
          <ArtifactHeader toolCall={toolCall} artifact={artifact} />
          <div
            className={classnames(styles.artifactBody, {
              [styles.artfactBodyPadding]: needsPadding,
            })}
          >
            <MessageContent
              body={artifact.body || ''}
              contentType={artifact.contentType || ''}
              toolCallData={toolCall?.content?.data || undefined}
            />
          </div>
        </React.Fragment>
      );
    }
  } else {
    hidden = true;
    removePopoverAttributes();
    controller.actionCloseArtifact();
  }

  if (!artifact) {
    return null;
  }

  const shouldFitContent = SHRINKING_CONTENT_TYPES.includes(
    artifact?.contentType || ''
  );

  return (
    <div ref={artifactPanelParentRef} className={styles.artifactPanelParent}>
      {!isFloating && (
        <ResizerHandle
          onResize={setWidth}
          onStartResize={() => setResizing(true)}
          onEndResize={() => setResizing(false)}
        />
      )}

      <div
        id={`artifact-panel-${toolCall?.uniqueId}`}
        className={classnames(styles.artifactPanel, 'floating', {
          [styles.floating]: isFloating,
          [styles.embedded]: ['full-screen', 'full-page'].includes(size),
          [styles.hidden]: hidden,
          [styles.resizing]: resizing,
          [styles.shrinkToContent]: shouldFitContent,
        })}
        style={{ width: width }}
        ref={artifactPanelRef}
      >
        {isFloating && (
          <ResizerHandle
            floating
            onResize={setWidth}
            onStartResize={() => setResizing(true)}
            onEndResize={() => setResizing(false)}
          />
        )}
        <div className={styles.artifactPanelContent}>{content}</div>
      </div>
    </div>
  );
};

export default ApplicationView(ArtifactPanel);
